import React from 'react'
import { useIntl } from 'react-intl'

import { Icon } from '../../atoms'
import { UrlNameInterface } from '../../../hooks/useUpload'
import { Button } from '../../../shadcn/components/ui/button'

interface FileUploadAreaProps {
  dragAndDropMessage: string
  allowedFileTypes: string[]
  multiple?: boolean
  files: (File | UrlNameInterface)[]
  uploadErrors: string[]
  fileInputRef: React.RefObject<HTMLInputElement>
  handleFileChange: (files: FileList | null) => void
  handleChooseFileClick: () => void
  handleRemoveFile: (index: number) => void
  handleDragOver: (event: React.DragEvent<HTMLDivElement>) => void
  handleDragLeave: () => void
  handleDrop: (event: React.DragEvent<HTMLDivElement>) => void
}

const FileUploadArea: React.FC<FileUploadAreaProps> = ({
  dragAndDropMessage,
  allowedFileTypes,
  multiple = true,
  files,
  uploadErrors,
  fileInputRef,
  handleChooseFileClick,
  handleRemoveFile,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleFileChange,
}) => {
  const intl = useIntl()

  return (
    <>
      {/* Conditionally render the upload area based on multiple and file count */}
      {(multiple || files.length === 0) && (
        <div
          className='mb-2 flex flex-1 flex-col items-center justify-center gap-4 rounded-lg border border-dashed p-6 align-middle'
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <Icon name='uploadThin' className='h-5 w-5' />
          <div className='text-[14px]'>{dragAndDropMessage}</div>
          <div className='flex w-full items-center justify-center gap-2'>
            <div className='border-gray-400 w-1/6 border-t'></div>
            <span className='text-gray-500'>
              {intl.formatMessage({
                id: 'ui.ModalUploadFile.or',
                defaultMessage: 'ou',
              })}
            </span>
            <div className='border-gray-400 w-1/6 border-t'></div>
          </div>

          {/* File Input */}
          <input
            type='file'
            accept={allowedFileTypes.map((type) => `.${type}`).join(', ')}
            multiple={multiple}
            onChange={(event) => handleFileChange(event.target.files)}
            ref={fileInputRef}
            style={{ display: 'none' }}
          />

          <Button variant='outline' onClick={handleChooseFileClick}>
            {intl.formatMessage({
              id: 'ui.ModalUploadFile.chooseFile',
              defaultMessage: 'Choisir un fichier',
            })}
          </Button>
        </div>
      )}

      {/* Display upload errors */}
      {uploadErrors.length > 0 && (
        <div className='text-red-500'>
          {uploadErrors.map((err, index) => (
            <div key={index}>{err}</div>
          ))}
        </div>
      )}

      {/* Display uploaded files */}
      {files.length > 0 && (
        <div className='flex flex-col gap-2'>
          {files.map((file, index) => (
            <div
              key={index}
              className='flex min-h-12 items-center justify-between rounded-lg border border-grey-700 p-2 px-4'
            >
              <div>{file.name}</div>
              <div onClick={() => handleRemoveFile(index)}>
                <Icon
                  name='cross-without-circle'
                  className='size-4 cursor-pointer'
                  color='black'
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  )
}

export default FileUploadArea
